<template>
  <div class="privacy-policy min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 py-4 mb-8">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="@/assets/logo.png" alt="Salasah" class="h-8 w-auto" />
            <span class="text-lg font-semibold text-gray-800">Salasah</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-4 py-8 max-w-4xl">
      <div class="bg-white rounded-lg shadow-sm p-8">
        <!-- Title -->
        <h1 class="text-3xl font-bold text-gray-900 mb-8 text-center">Privacy Policy</h1>

        <!-- Introduction -->
        <div class="mb-12 text-lg text-gray-700 leading-relaxed">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">In a nutshell</h2>
          <p class="mb-4">
            We take privacy and data security seriously. We are committed to being fair and transparent in the collection and use of your data. 
            The Privacy Policy below describes how we protect you and how we use your data to provide you with our services.
          </p>
        </div>

        <!-- Summary Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Summary</h2>
          <p class="text-gray-700 mb-4">
            We capture, manage and store data that may include your Personal Information as part of our normal operations and service offerings. 
            This Privacy Policy describes why we capture, how we use and the circumstances under which we may disclose this information.
          </p>
          <p class="text-gray-700 mb-4">
            By visiting or registering on our site, or by using our service, regardless of the manner or device made available and supported by us 
            through which you choose to do so, you are agreeing to our Privacy Policy, outlined below. We assume certain responsibilities and make 
            related commitments to you. We also clarify that we are not liable with respect to misuse of the site by you or others and other third 
            party risks beyond our control that may affect your Personal Information.
          </p>
          <p class="text-gray-700 mb-4">
            If you have concerns about this, you should contact us at <a href="mailto:help@Salasah.sa" class="text-[#0f4640] hover:text-[#1f9285]">help@Salasah.sa</a>
          </p>
          <p class="text-gray-700">
            By using our site or services, you agree to the terms of this privacy policy and you expressly consent to the processing of your personal 
            information according to this privacy policy. Your personal information may be processed by us or on our behalf in the country where it was 
            collected as well as other countries where laws regarding processing of personal information may be less stringent than the laws in your country.
          </p>
        </section>

        <!-- Personal Information Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">What is personal information?</h2>
          <p class="text-gray-700 mb-4">
            Personal Information includes your name, mailing address, phone number, e-mail address and demographic and other personally identifiable 
            information about you as an individual. If you are a user of our site, it will also include business information that you contribute 
            which concerns business and personal relationships between yourself and other individuals and organizations ("Bookkeeping Data").
          </p>
          <p class="text-gray-700">
            You may provide or upload Personal Information in order to enable us to provide you with our services. We may also ask for certain 
            Personal Information when you request information about or order our products and services, participate in our marketing, sales or other 
            promotional activities, or correspond with us. If you are in a part of our site that requires you to provide your Personal Information, 
            it will be clear what Personal Information we are requesting.
          </p>
        </section>

        <!-- Data Collection Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Why do we collect your information?</h2>
          <div class="text-gray-700">
            <p class="mb-4">We collect, use and disclose information that can identify you ("Personal Information") so that we can:</p>
            <ul class="list-disc ml-6 space-y-2">
              <li>Establish your identity</li>
              <li>Provide to you the services offered by the Site and bill any amounts due from you</li>
              <li>Troubleshoot and resolve problems</li>
              <li>Improve the usability, performance and effectiveness of our Site and business</li>
              <li>Provide you with news, updates, and reminders related to our services</li>
              <li>Enforce our Terms of Service</li>
              <li>Protect you and us from error and fraud</li>
              <li>Understand your needs and eligibility for products and services</li>
              <li>Comply with legal and regulatory requirements</li>
            </ul>
            <p class="mt-4 mb-4">We only collect Personal Information that we consider necessary for achieving these purposes.</p>
            <p class="mb-4">
              We may also collect, process, store and analyze anonymous information about you obtained from your browser or through the use of "cookies" 
              or other tools that track, measure and analyze the behaviors and usage patterns of visitors to our site and users of our services. We may 
              use third party tracking services to track and analyze such anonymous information from users of our service, and such third parties may 
              use cookies to help track user behavior.
            </p>
            <p class="mb-4">
              We may send you communications via email about our service. You can opt out of receiving communications from us by changing your notification 
              settings on our website. Please note that even if you unsubscribe or opt-out, we may still send you urgent service related communications 
              (e.g., if your credit card payment was declined). If you do not wish to receive them, you must delete your account with us.
            </p>
          </div>
        </section>

        <!-- Data Sharing Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Who do we share your personal information with?</h2>
          <p class="text-gray-700 mb-4">
            Except as set forth in this privacy policy, we do not sell or release your Personal Information to third parties without your consent. 
            However, we reserve the right to gather, analyze, aggregate and transform non-personally identifiable information sourced from all or any 
            portion of our visitors and users, and to make this information available at our site or through other means, channels or partners at such 
            time and on such terms as we believe are appropriate.
          </p>
          <p class="text-gray-700 mb-4">
            We retain the right to manage our operations, including using service providers of our own choosing, to facilitate the development, hosting 
            and ongoing support of our services to meet our business goals. For example, we may use the services of third-party companies to host our 
            services and these services may or may not be located in the same jurisdictions or regions as the customers using our services.
          </p>
          <p class="text-gray-700 mb-4">
            Our employees, consultants, contractors, suppliers and partners, if authorized, may have access to our customer information or the systems 
            storing it as part of and solely for the purposes of performing their jobs, or within the scope of their agreements with us. In all cases, 
            we use reasonable efforts to ensure that your Personal Information, as part of our confidential business information, is protected under 
            contracts with such persons.
          </p>
          <div class="mb-4">
            <p class="text-gray-700 mb-2">Some of our service providers include:</p>
            <ul class="list-disc ml-6 text-gray-700">
              <li>Web Services (web hosting)</li>
              <li>(web hosting)</li>
            </ul>
          </div>
          <p class="text-gray-700 mb-4">
            While we take care to use only service providers that we believe are reputable and able to live up to our and your expectations, including 
            about the handling of confidential information, Salasah is not liable for unlawful or unauthorized use of Personal Information by any of 
            such persons unless such unlawful use was a direct result of our gross negligence or wilful misconduct.
          </p>
        </section>

        <!-- Third Party Information Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Your use of the personal information of others</h2>
          <p class="text-gray-700 mb-4">
            If you choose to provide Personal Information of any third party, we will assume that you have the third party's permission to provide us 
            that information. Examples include Site referrals to friends. This information will not be used for any purposes other than those described above.
          </p>
          <p class="text-gray-700 mb-4">
            The site may contain links to other websites that are not owned or controlled by us. We have no control over, do not review and are not 
            responsible for the privacy policies of or content displayed on such other websites. When you click on such a link, you will leave our 
            service and go to another site. During this process, another entity may collect Personal Information from you, and we do not collect 
            information from you that you submit on such third party sites.
          </p>
        </section>

        <!-- Security Policy Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">What is our security policy?</h2>
          <p class="text-gray-700 mb-4">
            We use commercially reasonable efforts to ensure that Personal Information is secure, including https protocol and encryption. In order to 
            prevent unauthorised access or disclosure, we put in place suitable physical, electronic and managerial procedures to safeguard and secure 
            the information we collect, as well as the modes of access to it. While we strive to protect your Personal Information, we cannot warrant 
            the security of any information you transmit to us. More information about security is available in our Terms of Service.
          </p>
        </section>

        <!-- Account Management Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">How can you access, correct and update your personal information?</h2>
          <p class="text-gray-700 mb-4">
            Much of your Personal Information is available to you through your account. To review and update your Personal Information to ensure it is 
            accurate, you may login into your account to make the changes, or you may contact us. If we are unable to provide access or disclose 
            information to you, we will explain why, subject to any legal or regulatory restrictions.
          </p>
        </section>

        <!-- Account Deletion Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">How can you delete your account?</h2>
          <p class="text-gray-700 mb-4">
            All Personal Information linked to your account will be deleted at your request as soon as reasonably possible in accordance with the law. 
            When you make a request to delete your account, your data will be deleted from our production servers, and you will no longer be able to 
            access your account. However, we may retain your Personal Information to comply with laws, prevent fraud, resolve disputes, troubleshoot 
            problems, assist with any investigations, enforce our terms of service, and take other actions otherwise permitted by law.
          </p>
          <p class="text-gray-700 mb-4">
            Please remember, however, if we have already disclosed some of your Personal Information to third parties, such as your accountant, we 
            cannot access that Personal Information any longer and cannot force the deletion or modification of any such information by the parties 
            to whom we have made those disclosures.
          </p>
          <p class="text-gray-700">
            Aggregated or anonymous data derived from your Personal Information, which cannot be traced back to your account, may remain on our 
            production servers indefinitely. We reserve the right to use any aggregated or anonymous data derived from or incorporating your Personal Information.
          </p>
        </section>

        <!-- Updates Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">How will you learn of updates to our privacy rules?</h2>
          <p class="text-gray-700 mb-4">
            We may update this Privacy Policy to reflect changes to our privacy and data management practices. If we make any material changes we will 
            notify you by email (sent to the e-mail address specified in your account) or by means of a notice on this Site prior to the change 
            becoming effective. We encourage you to periodically review this page for the latest information on our privacy practices.
          </p>
        </section>

        <!-- Children's Privacy Section -->
        <section class="border-b border-gray-100 pb-8 mb-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Children's Privacy</h2>
          <p class="text-gray-700 mb-4">
            Our service is not directed to children and children are not eligible to use it. Protecting the privacy of children is very important to us. 
            We do not collect or maintain Personal Information from people we actually know are under 13 years of age, and no part of our service is 
            designed to attract people under 13 years of age. If we later learn that a user is under 13 years of age, we will take steps to remove 
            that user's Personal Information from our databases and to prevent the user from utilizing the service.
          </p>
        </section>

        <!-- Contact Section -->
        <section class="bg-[#faf6df] rounded-lg p-8">
          <h2 class="text-2xl font-bold text-[#0f4640] mb-6">Who should you contact with comments and questions?</h2>
          <div class="text-gray-700">
            <p class="mb-6 text-lg">
              Salasah welcomes your comments and questions regarding this Privacy Policy and the use of your Personal Information. 
              If you have questions, concerns or if you would like more detailed information please contact us at:
            </p>
            <div class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
              <a href="mailto:help@Salasah.sa" class="flex items-center gap-3 text-[#0f4640] hover:text-[#0f4640]/80 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span class="text-lg font-medium">help@Salasah.sa</span>
              </a>
            </div>
          </div>
        </section>

        <!-- Last Update -->
        <div class="mt-12 pt-8 border-t border-gray-100 text-center text-gray-500 text-sm">
          Last updated: January 28, 2024
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy'
}
</script>

<style scoped>
.privacy-policy {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.list-disc {
  list-style-type: disc;
}

.list-disc li {
  margin-left: 1.5rem;
  padding-left: 0.5rem;
}
</style>